// src/pages/ChatPage.js
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { apiRequest } from "../services/api";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import { FaWhatsapp, FaComments } from "react-icons/fa";

function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useContext(LanguageContext);

  const [messages, setMessages] = useState([]);
const [threadId, setThreadId] = useState(null);
const [text, setText] = useState("");
const [chatLoading, setChatLoading] = useState(true);
const [sending, setSending] = useState(false);
const [showWhatsappCTA, setShowWhatsappCTA] = useState(true);

  const chatBodyRef = useRef(null);

  const whatsappMessage = lang === "EN" ? "I need help" : "أحتاج مساعدة";

  const whatsappLink = `https://wa.me/96181446356?text=${encodeURIComponent(
  whatsappMessage
)}`;

  const TEXT = {
    EN: {
      mainPage: "Main page",
      contact: "Contact",
      account: "Account",
      title: "Support chat",
      subtitle:
        "Chat with our support team and follow up on your previous messages.",
      placeholder: "Type your message...",
      send: "SEND",
      noThread: "No support conversation found yet.",
      whatsapp: "Need faster help? Contact us on WhatsApp.",
      loading: "Loading support chat...",
startConversation: "Start a conversation",
goToContact: "Go to contact page",
sending: "SENDING...",
    },
    AR: {
      mainPage: "الصفحة الرئيسية",
      loading: "جاري تحميل محادثة الدعم...",
startConversation: "ابدأ محادثة",
goToContact: "الذهاب إلى صفحة التواصل",
sending: "جارٍ الإرسال...",
      contact: "تواصل معنا",
       account: "الحساب",
      title: "محادثة الدعم",
      subtitle: "تحدث مع فريق الدعم وتابع رسائلك السابقة.",
      placeholder: "اكتب رسالتك...",
      send: "إرسال",
      noThread: "لا توجد محادثة دعم حالياً.",
      whatsapp: "تحتاج مساعدة أسرع؟ تواصل معنا عبر واتساب.",
    },
  }[lang];

  /* Load thread */
 useEffect(() => {
  let mounted = true;
  let interval;

  async function load() {
    const token = localStorage.getItem("esill-token");

    if (!token) {
      localStorage.setItem("esill-open-login", "1");
      navigate("/create-account", { replace: true });
      return;
    }

    try {
      setChatLoading(true);

      const list = await apiRequest("/contact-messages/mine");
      const safeList = Array.isArray(list) ? list : [];

      if (!mounted) return;

      const targetId = location.state?.messageId || safeList?.[0]?.id;

      if (!targetId) {
        setThreadId(null);
        setMessages([]);
        return;
      }

      setThreadId(targetId);

      const thread = safeList.find((m) => Number(m.id) === Number(targetId));

      setMessages(Array.isArray(thread?.replies) ? thread.replies : []);

      await apiRequest(`/contact-messages/${targetId}/read`, {
        method: "POST",
      }).catch(() => {});

      interval = setInterval(async () => {
        try {
          const fresh = await apiRequest("/contact-messages/mine");
          const freshList = Array.isArray(fresh) ? fresh : [];
          const updated = freshList.find(
            (m) => Number(m.id) === Number(targetId)
          );

          if (mounted && updated) {
            setMessages(Array.isArray(updated.replies) ? updated.replies : []);
          }
        } catch {}
      }, 4000);
    } catch (e) {
      if (mounted) {
        setThreadId(null);
        setMessages([]);
      }
    } finally {
      if (mounted) {
        setChatLoading(false);
      }
    }
  }

  load();

  return () => {
    mounted = false;
    if (interval) clearInterval(interval);
  };
}, [location.state?.messageId, navigate]);


 /* Auto scroll only inside chat box, not the full page */
useEffect(() => {
  const el = chatBodyRef.current;
  if (!el) return;

  el.scrollTo({
    top: el.scrollHeight,
    behavior: "smooth",
  });
}, [messages]);

  /* Send message */
  const send = async () => {
  if (!text.trim()) return;
  if (!threadId || sending) return;

  const messageBody = text.trim();

  try {
    setSending(true);

    await apiRequest(`/contact-messages/${threadId}/user-reply`, {
      method: "POST",
      body: JSON.stringify({ body: messageBody }),
    });

    setMessages((m) => [
      ...m,
      {
        sender_type: "user",
        body: messageBody,
      },
    ]);

    setText("");
  } catch (err) {
    console.error("Failed to send chat message", err);
  } finally {
    setSending(false);
  }
};

  const inputClass = `
    flex-1
    h-11
    border border-gray-400
    bg-white
    px-3
    text-sm
    outline-none
    focus:border-black
    transition
    disabled:opacity-50
  `;

  const buttonClass = `
    h-11
    px-5
    rounded-md
    border-2 border-black
    bg-white
    text-black
    text-sm font-semibold tracking-wide
    hover:bg-black hover:text-white
    active:scale-[0.99]
    transition
    disabled:opacity-50
    disabled:cursor-not-allowed
  `;

  const ChatPageSkeleton = () => (
  <div
    className="w-full min-h-screen bg-white overflow-x-hidden"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    <main className="bg-white px-4 md:px-8 md:pt-[175px]">
      <div className="h-4 w-[280px] bg-gray-200 animate-pulse rounded mb-6" />
      <div className="h-6 w-[180px] bg-gray-200 animate-pulse rounded mb-3" />
      <div className="h-[1px] bg-gray-200 mx-1 md:mx-4 mb-8" />

      <div className="max-w-5xl mx-auto pb-12">
        <div className="mb-8">
          <div className="h-5 w-[420px] max-w-full bg-gray-200 animate-pulse rounded" />
        </div>

        <div className="border border-gray-200 bg-white rounded-xl shadow-[0_10px_24px_rgba(0,0,0,0.04)] overflow-hidden">
          <div className="border-b border-gray-200 px-4 md:px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-200 animate-pulse rounded-md" />

            <div className="flex-1">
              <div className="h-5 w-[150px] bg-gray-200 animate-pulse rounded" />
              <div className="h-3 w-[260px] bg-gray-200 animate-pulse rounded mt-2" />
            </div>
          </div>

          <div className="h-[56vh] md:h-[520px] px-4 md:px-5 py-5 space-y-4 bg-white">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`flex ${i % 2 === 0 ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`
                    h-[46px]
                    rounded-xl
                    bg-gray-200
                    animate-pulse
                    ${i % 2 === 0 ? "w-[55%]" : "w-[48%]"}
                  `}
                />
              </div>
            ))}
          </div>

          <div className="border-t border-gray-200 bg-white px-4 md:px-5 py-4">
            <div className="flex gap-3">
              <div className="h-11 flex-1 bg-gray-200 animate-pulse rounded" />
              <div className="h-11 w-[90px] bg-gray-200 animate-pulse rounded" />
            </div>
          </div>
        </div>

        <div className="mt-6 h-[120px] border border-gray-200 bg-white rounded-xl p-5 shadow-[0_10px_24px_rgba(0,0,0,0.04)]">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gray-200 animate-pulse rounded-md" />
            <div className="flex-1">
              <div className="h-4 w-[260px] bg-gray-200 animate-pulse rounded" />
              <div className="mt-4 h-11 w-[130px] bg-gray-200 animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    </main>

    <Footer />
  </div>
);

const ChatEmptyState = () => (
  <div className="h-full flex items-center justify-center text-center px-4">
    <div className="max-w-[420px]">
      <div
        className="
          mx-auto
          w-[62px] h-[62px]
          rounded-full
          border border-black
          flex items-center justify-center
          text-black
          mb-5
        "
      >
        <FaComments className="text-[24px]" />
      </div>

      <h3 className="text-[22px] md:text-[26px] font-bold text-black">
        {TEXT.noThread}
      </h3>

      <p className="mt-3 text-[14px] md:text-[15px] leading-7 text-gray-600">
        {lang === "AR"
          ? "أرسل لنا رسالة من صفحة التواصل لبدء محادثة دعم جديدة."
          : "Send us a message from the contact page to start a new support conversation."}
      </p>

      <button
        type="button"
        onClick={() => navigate("/contact")}
        className="
          mt-7
          h-11
          px-6
          border-2 border-black
          rounded-md
          bg-white
          text-black
          text-sm font-semibold tracking-wide
          hover:bg-black hover:text-white
          transition
        "
      >
        {TEXT.goToContact}
      </button>
    </div>
  </div>
);

if (chatLoading) {
  return <ChatPageSkeleton />;
}

  return (
    <div
      className="w-full min-h-screen bg-white overflow-x-hidden"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />

      <main className="bg-white px-4 md:px-8 md:pt-[175px]">
        {/* BREADCRUMB */}
<div className="text-[12px] md:text-sm text-gray-900 mb-6">
  <button
    type="button"
    onClick={() => navigate("/")}
    className="hover:text-black hover:underline"
  >
    {TEXT.mainPage}
  </button>

  <span className="mx-2">›</span>

  <button
    type="button"
    onClick={() => navigate("/account")}
    className="hover:text-black hover:underline"
  >
    {TEXT.account}
  </button>

  <span className="mx-2">›</span>

  <button
    type="button"
    onClick={() => navigate("/contact")}
    className="hover:text-black hover:underline"
  >
    {TEXT.contact}
  </button>

  <span className="mx-2">›</span>

  <span className="text-gray-900">{TEXT.title}</span>
</div>

        {/* TITLE */}
        <div className={`${lang === "AR" ? "text-right" : "text-left"} mb-2`}>
          <h1 className="text-[16px] md:text-[20px] font-normal tracking-wide text-gray-900 uppercase">
            {TEXT.title}
          </h1>
        </div>

        {/* LINE */}
        <div className="h-[1px] bg-gray-300 mx-1 md:mx-4 mb-8" />

        {/* CONTENT */}
        <div className="max-w-5xl mx-auto pb-12">
          <div className="mb-8">
            <h2 className="hidden text-[30px] md:text-[38px] font-black text-black leading-tight mb-3">
              {TEXT.title}
            </h2>

            <p className="text-sm md:text-base text-gray-600 max-w-2xl leading-7">
              {TEXT.subtitle}
            </p>
          </div>

          <div
            className="
              border border-gray-200
              bg-white
              rounded-xl
              shadow-[0_10px_24px_rgba(0,0,0,0.04)]
              overflow-hidden
            "
          >
            {/* CHAT TOP */}
            <div className="border-b border-gray-200 px-4 md:px-5 py-4 flex items-center gap-3">
              <span className="w-10 h-10 border border-gray-300 rounded-md flex items-center justify-center shrink-0 text-black">
                <FaComments className="text-[17px]" />
              </span>

              <div>
                <h3 className="text-[16px] font-bold text-black">
                  {TEXT.title}
                </h3>

                <p className="text-xs text-gray-500 mt-0.5">
                  {threadId ? TEXT.subtitle : TEXT.noThread}
                </p>
              </div>
            </div>

            {/* CHAT BODY */}
            <div
  ref={chatBodyRef}
  className="h-[56vh] md:h-[520px] overflow-y-auto px-4 md:px-5 py-5 space-y-4 bg-white"
>
              {!threadId && <ChatEmptyState />}

              {messages.map((m, i) => {
                const isUser = m.sender_type === "user";

                return (
                  <div
                    key={i}
                    className={`flex ${
                      isUser ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`
                        max-w-[78%] md:max-w-[65%]
                        text-sm
                        px-4 py-3
                        rounded-xl
                        border
                        leading-6
                        ${
                          isUser
                            ? "bg-black text-white border-black"
                            : "bg-white text-gray-900 border-gray-300"
                        }
                      `}
                    >
                      {m.body}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* INPUT AREA */}
            <div className="border-t border-gray-200 bg-white px-4 md:px-5 py-4">
              <div className="flex gap-3">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder={TEXT.placeholder}
                  disabled={!threadId || sending}
                  className={inputClass}
                />

                <button
                  type="button"
                  onClick={send}
                  disabled={!threadId || sending}
                  className={buttonClass}
                >
                  {sending ? TEXT.sending : TEXT.send}
                </button>
              </div>
            </div>
          </div>

          {/* WHATSAPP CARD */}
          <div className="mt-6 border border-gray-200 bg-white rounded-xl p-5 shadow-[0_10px_24px_rgba(0,0,0,0.04)]">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 border border-gray-300 rounded-md flex items-center justify-center shrink-0 text-black">
                <FaWhatsapp className="text-[18px]" />
              </span>

              <div className="flex-1">
                <p className="text-sm text-gray-700 leading-6">
                  {TEXT.whatsapp}
                </p>

                <button
                  type="button"
                  onClick={() => window.open(whatsappLink, "_blank")}
                  className="mt-4 h-11 px-5 rounded-md border-2 border-black bg-white text-black text-sm font-semibold tracking-wide hover:bg-black hover:text-white transition"
                >
                  WHATSAPP
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* FLOATING WHATSAPP CTA */}
      {showWhatsappCTA && (
        <div
          className="fixed z-[9999] bottom-24 right-5 flex flex-col items-center gap-2"
          dir="ltr"
        >
          <button
            type="button"
            onClick={() => setShowWhatsappCTA(false)}
            className="
              w-7 h-7 rounded-full
              bg-white shadow-md
              flex items-center justify-center
              text-black text-sm
            "
          >
            ×
          </button>

          <a
            href={whatsappLink}
            target="_blank"
            rel="noreferrer"
            className="
              w-14 h-14 rounded-full
              flex items-center justify-center
              shadow-xl
              active:scale-95 transition
            "
            style={{ backgroundColor: "#25D366" }}
          >
            <img
              src="/icons/whatsapp.png"
              alt="WhatsApp"
              className="w-7 h-7"
            />
          </a>
        </div>
      )}
    </div>
  );
}

export default ChatPage;